import { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, FileText, X } from 'lucide-react';
import { Button } from './ui/button';

// One-pager content for PDF generation
const onePagerContent = {
  headline: 'Structured AI Automation for Operations-Driven Companies',
  tagline: 'Pyrtex builds intake systems, workflow automation, and supervisory control layers that help businesses handle inbound complexity with consistency and visibility.',
  
  capabilities: [
    {
      title: 'AI Intake Systems',
      description: 'Structured intake and triage for incoming requests, forms, messages, and operational inputs.'
    },
    {
      title: 'Workflow Automation', 
      description: 'Automation layers that reduce repetitive processing and support consistent internal workflows.'
    },
    {
      title: 'Communication Automation',
      description: 'Smart interfaces that guide users, collect structured information, and reduce manual response load.'
    },
    {
      title: 'Supervisory Control',
      description: 'Operational oversight layers for monitoring, alerting, escalation, and system-level visibility.'
    }
  ],
  
  industries: [
    'Recruitment & Staffing',
    'Logistics & Supply Chain', 
    'Manufacturing & Operations',
    'Professional Services'
  ],
  
  approach: [
    'Process-first design built around your actual workflows',
    'Phased implementation — pilot first, then expand',
    'Visibility and control built in from day one',
    'Business-grade delivery with proper documentation'
  ],
  
  contact: {
    email: 'hello@pyrtex.com',
    website: 'pyrtex.com'
  }
};

export const OnePager = () => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleDownload = () => {
    // Create printable version
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Pyrtex - AI Automation One-Pager</title>
        <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&family=Inter:wght@400;500&display=swap" rel="stylesheet">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: A4; margin: 0; }
          body { 
            font-family: 'Inter', sans-serif; 
            color: #0f172a;
            background: #fff;
            line-height: 1.5;
          }
          .page {
            width: 210mm;
            min-height: 297mm;
            padding: 20mm 20mm;
            margin: 0 auto;
            background: #fff;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 1px solid #e2e8f0;
          }
          .logo {
            font-family: 'Manrope', sans-serif;
            font-size: 28px;
            font-weight: 700;
            color: #0f172a;
          }
          .contact-info {
            text-align: right;
            font-size: 11px;
            color: #64748b;
          }
          .hero {
            margin-bottom: 28px;
          }
          .headline {
            font-family: 'Manrope', sans-serif;
            font-size: 26px;
            font-weight: 700;
            color: #0f172a;
            margin-bottom: 12px;
            line-height: 1.2;
          }
          .tagline {
            font-size: 13px;
            color: #475569;
            max-width: 90%;
          }
          .section {
            margin-bottom: 24px;
          }
          .section-title {
            font-family: 'Manrope', sans-serif;
            font-size: 10px;
            font-weight: 600;
            color: #94a3b8;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            margin-bottom: 12px;
          }
          .capabilities-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
          }
          .capability {
            padding: 14px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
          }
          .capability-title {
            font-family: 'Manrope', sans-serif;
            font-size: 13px;
            font-weight: 600;
            color: #0f172a;
            margin-bottom: 6px;
          }
          .capability-desc {
            font-size: 11px;
            color: #64748b;
            line-height: 1.5;
          }
          .industries-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }
          .industry-tag {
            padding: 6px 12px;
            background: #0f172a;
            color: #fff;
            font-size: 11px;
            font-weight: 500;
          }
          .approach-list {
            list-style: none;
          }
          .approach-item {
            font-size: 12px;
            color: #334155;
            padding: 8px 0;
            border-bottom: 1px solid #f1f5f9;
            display: flex;
            align-items: flex-start;
            gap: 10px;
          }
          .approach-item:last-child {
            border-bottom: none;
          }
          .check {
            color: #0f172a;
            font-weight: 600;
            flex-shrink: 0;
          }
          .footer {
            margin-top: 24px;
            padding-top: 16px;
            border-top: 1px solid #e2e8f0;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .cta {
            font-family: 'Manrope', sans-serif;
            font-size: 14px;
            font-weight: 600;
            color: #0f172a;
          }
          .footer-contact {
            font-size: 11px;
            color: #64748b;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .page { width: 100%; min-height: auto; padding: 15mm; }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="header">
            <div class="logo">Pyrtex</div>
            <div class="contact-info">
              <div>${onePagerContent.contact.email}</div>
              <div>${onePagerContent.contact.website}</div>
            </div>
          </div>
          
          <div class="hero">
            <h1 class="headline">${onePagerContent.headline}</h1>
            <p class="tagline">${onePagerContent.tagline}</p>
          </div>
          
          <div class="section">
            <h2 class="section-title">Core Capabilities</h2>
            <div class="capabilities-grid">
              ${onePagerContent.capabilities.map(cap => `
                <div class="capability">
                  <div class="capability-title">${cap.title}</div>
                  <div class="capability-desc">${cap.description}</div>
                </div>
              `).join('')}
            </div>
          </div>
          
          <div class="section">
            <h2 class="section-title">Industries We Serve</h2>
            <div class="industries-list">
              ${onePagerContent.industries.map(ind => `
                <span class="industry-tag">${ind}</span>
              `).join('')}
            </div>
          </div>
          
          <div class="section">
            <h2 class="section-title">Our Approach</h2>
            <ul class="approach-list">
              ${onePagerContent.approach.map(item => `
                <li class="approach-item">
                  <span class="check">→</span>
                  <span>${item}</span>
                </li>
              `).join('')}
            </ul>
          </div>
          
          <div class="footer">
            <div class="cta">Ready to discuss your workflow?</div>
            <div class="footer-contact">${onePagerContent.contact.email}</div>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Trigger print dialog after content loads
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  return (
    <>
      {/* Download Button - positioned fixed on the page */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.4 }}
        className="fixed bottom-6 left-6 z-40"
      >
        <Button
          onClick={() => setIsPreviewOpen(true)}
          data-testid="download-onepager-btn"
          className="bg-slate-900 text-white hover:bg-slate-800 text-xs font-medium px-4 py-2.5 rounded-sm shadow-lg flex items-center gap-2"
        >
          <FileText size={14} strokeWidth={1.5} />
          Download One-Pager
        </Button>
      </motion.div>

      {/* Preview Modal */}
      {isPreviewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-sm shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 font-['Manrope']">
                  Pyrtex One-Pager
                </h3>
                <p className="text-sm text-slate-500">
                  PDF overview for prospects and internal sharing
                </p>
              </div>
              <button
                onClick={() => setIsPreviewOpen(false)}
                className="p-2 hover:bg-slate-100 rounded-sm transition-colors"
              >
                <X size={20} className="text-slate-500" />
              </button>
            </div>

            {/* Modal Content - Preview */}
            <div className="p-6 overflow-y-auto max-h-[60vh] bg-slate-50">
              <div className="bg-white border border-slate-200 p-8 shadow-sm">
                {/* Preview Header */}
                <div className="flex justify-between items-start mb-6 pb-4 border-b border-slate-100">
                  <span className="text-2xl font-bold text-slate-900 font-['Manrope']">Pyrtex</span>
                  <div className="text-right text-xs text-slate-500">
                    <div>{onePagerContent.contact.email}</div>
                    <div>{onePagerContent.contact.website}</div>
                  </div>
                </div>

                {/* Preview Content */}
                <h2 className="text-xl font-bold text-slate-900 font-['Manrope'] mb-3 leading-tight">
                  {onePagerContent.headline}
                </h2>
                <p className="text-sm text-slate-600 mb-6">
                  {onePagerContent.tagline}
                </p>

                {/* Capabilities Preview */}
                <div className="mb-6">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-3">
                    Core Capabilities
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {onePagerContent.capabilities.map((cap, i) => (
                      <div key={i} className="p-3 bg-slate-50 border border-slate-100">
                        <p className="text-xs font-semibold text-slate-900 mb-1">{cap.title}</p>
                        <p className="text-[11px] text-slate-500 leading-relaxed">{cap.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Industries Preview */}
                <div className="mb-6">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-3">
                    Industries
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {onePagerContent.industries.map((ind, i) => (
                      <span key={i} className="px-2 py-1 bg-slate-900 text-white text-[10px] font-medium">
                        {ind}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-white">
              <Button
                onClick={() => setIsPreviewOpen(false)}
                variant="outline"
                className="text-xs rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDownload}
                data-testid="download-pdf-btn"
                className="bg-slate-900 text-white hover:bg-slate-800 text-xs rounded-sm flex items-center gap-2"
              >
                <Download size={14} />
                Download as PDF
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};
